// Template definitions. Each template has a renderer type and field definitions.
// Renderer types: 'document' (long-form text), 'invoice', 'receipt', 'resume'

export const CATEGORIES = [
  { slug: 'legal', name: 'Legal', icon: 'Scale', tint: 'bg-amber-100 text-amber-900' },
  { slug: 'business', name: 'Business', icon: 'Briefcase', tint: 'bg-orange-100 text-orange-900' },
  { slug: 'personal', name: 'Personal', icon: 'User', tint: 'bg-rose-100 text-rose-900' },
  { slug: 'hr', name: 'HR & Work', icon: 'Users', tint: 'bg-emerald-100 text-emerald-900' },
  { slug: 'real-estate', name: 'Real Estate', icon: 'Home', tint: 'bg-sky-100 text-sky-900' },
  { slug: 'education', name: 'Education', icon: 'GraduationCap', tint: 'bg-violet-100 text-violet-900' },
];

// helper for date today
const today = () => new Date().toISOString().slice(0, 10);

export const TEMPLATES = [
  // ───────── LEGAL ─────────
  {
    id: 'nda',
    title: 'Non-Disclosure Agreement',
    category: 'legal',
    description: 'Mutual NDA to protect confidential information between two parties.',
    icon: 'FileLock2',
    popular: true,
    type: 'document',
    fields: [
      { key: 'partyA', label: 'Disclosing Party (Name)', type: 'text', required: true, placeholder: 'Acme Inc.' },
      { key: 'partyB', label: 'Receiving Party (Name)', type: 'text', required: true, placeholder: 'Jane Doe' },
      { key: 'effectiveDate', label: 'Effective Date', type: 'date', default: today() },
      { key: 'purpose', label: 'Purpose of Disclosure', type: 'textarea', placeholder: 'Discussion regarding potential business collaboration in software consulting.' },
      { key: 'duration', label: 'Confidentiality Duration (years)', type: 'number', default: 2 },
      { key: 'state', label: 'Governing State/Country', type: 'text', default: 'California, USA' },
    ],
    body: [
      { type: 'h1', text: 'NON-DISCLOSURE AGREEMENT' },
      { type: 'p', text: 'This Non-Disclosure Agreement ("Agreement") is entered into on {{effectiveDate}} by and between {{partyA}} ("Disclosing Party") and {{partyB}} ("Receiving Party").' },
      { type: 'h2', text: '1. Purpose' },
      { type: 'p', text: 'The parties wish to explore the following business opportunity: {{purpose}}' },
      { type: 'h2', text: '2. Confidential Information' },
      { type: 'p', text: 'The Receiving Party agrees to hold in strict confidence and not disclose any proprietary or confidential information received from the Disclosing Party, including but not limited to trade secrets, business plans, financial data, customer lists, and technical information.' },
      { type: 'h2', text: '3. Term' },
      { type: 'p', text: 'This Agreement shall remain in effect for {{duration}} year(s) from the Effective Date. Confidentiality obligations shall survive termination of this Agreement.' },
      { type: 'h2', text: '4. Governing Law' },
      { type: 'p', text: 'This Agreement shall be governed by and construed in accordance with the laws of {{state}}.' },
      { type: 'h2', text: '5. Signatures' },
      { type: 'sign', labels: ['Disclosing Party: {{partyA}}', 'Receiving Party: {{partyB}}'] },
    ],
  },
  {
    id: 'bill-of-sale',
    title: 'Bill of Sale',
    category: 'legal',
    description: 'Transfer ownership of personal property such as a vehicle or item.',
    icon: 'ScrollText',
    type: 'document',
    fields: [
      { key: 'seller', label: 'Seller Name', type: 'text', required: true },
      { key: 'buyer', label: 'Buyer Name', type: 'text', required: true },
      { key: 'itemDescription', label: 'Item Description', type: 'textarea', placeholder: '2018 Honda Civic LX, VIN: 1HGFC2F59JH123456, 45,000 miles, blue' },
      { key: 'amount', label: 'Sale Amount ($)', type: 'number', default: 5000 },
      { key: 'saleDate', label: 'Date of Sale', type: 'date', default: today() },
    ],
    body: [
      { type: 'h1', text: 'BILL OF SALE' },
      { type: 'p', text: 'On {{saleDate}}, {{seller}} ("Seller") agrees to sell and {{buyer}} ("Buyer") agrees to purchase the following described property:' },
      { type: 'p', text: '{{itemDescription}}' },
      { type: 'p', text: 'For the sum of ${{amount}} USD, paid in full. Seller certifies that they are the lawful owner of said property and that it is free from all liens and encumbrances.' },
      { type: 'sign', labels: ['Seller: {{seller}}', 'Buyer: {{buyer}}'] },
    ],
  },

  // ───────── BUSINESS ─────────
  {
    id: 'invoice',
    title: 'Professional Invoice',
    category: 'business',
    description: 'Itemized invoice with totals, tax, and payment instructions.',
    icon: 'Receipt',
    popular: true,
    type: 'invoice',
    fields: [
      { key: 'businessName', label: 'Your Business Name', type: 'text', required: true, placeholder: 'Acme Studio' },
      { key: 'businessAddress', label: 'Your Address', type: 'textarea', placeholder: '123 Main St\nSan Francisco, CA 94103' },
      { key: 'businessEmail', label: 'Your Email', type: 'text', placeholder: 'hello@acme.com' },
      { key: 'invoiceNumber', label: 'Invoice #', type: 'text', default: 'INV-001' },
      { key: 'invoiceDate', label: 'Invoice Date', type: 'date', default: today() },
      { key: 'dueDate', label: 'Due Date', type: 'date' },
      { key: 'clientName', label: 'Bill To (Client Name)', type: 'text', required: true },
      { key: 'clientAddress', label: 'Client Address', type: 'textarea' },
      { key: 'items', label: 'Line Items', type: 'lineitems', default: [
        { description: 'Design services', quantity: 10, rate: 100 },
      ]},
      { key: 'taxRate', label: 'Tax Rate (%)', type: 'number', default: 0 },
      { key: 'notes', label: 'Notes / Payment Instructions', type: 'textarea', placeholder: 'Payment due within 14 days. Bank transfer to...' },
    ],
  },
  {
    id: 'receipt',
    title: 'Payment Receipt',
    category: 'business',
    description: 'Simple receipt for goods or services paid in full.',
    icon: 'BadgeCheck',
    type: 'invoice',
    fields: [
      { key: 'businessName', label: 'Business Name', type: 'text', required: true },
      { key: 'businessAddress', label: 'Business Address', type: 'textarea' },
      { key: 'businessEmail', label: 'Email', type: 'text' },
      { key: 'invoiceNumber', label: 'Receipt #', type: 'text', default: 'RCP-001' },
      { key: 'invoiceDate', label: 'Date', type: 'date', default: today() },
      { key: 'clientName', label: 'Received From', type: 'text', required: true },
      { key: 'clientAddress', label: 'Customer Address', type: 'textarea' },
      { key: 'items', label: 'Items', type: 'lineitems', default: [
        { description: 'Consulting (1 hour)', quantity: 1, rate: 150 },
      ]},
      { key: 'taxRate', label: 'Tax Rate (%)', type: 'number', default: 0 },
      { key: 'notes', label: 'Note', type: 'textarea', default: 'Paid in full. Thank you for your business.' },
    ],
    receipt: true,
  },
  {
    id: 'purchase-order',
    title: 'Purchase Order',
    category: 'business',
    description: 'Formal purchase order to authorize a transaction with a vendor.',
    icon: 'ClipboardList',
    type: 'invoice',
    fields: [
      { key: 'businessName', label: 'Buyer (Your Company)', type: 'text', required: true },
      { key: 'businessAddress', label: 'Buyer Address', type: 'textarea' },
      { key: 'businessEmail', label: 'Buyer Email', type: 'text' },
      { key: 'invoiceNumber', label: 'PO Number', type: 'text', default: 'PO-001' },
      { key: 'invoiceDate', label: 'Order Date', type: 'date', default: today() },
      { key: 'dueDate', label: 'Requested Delivery Date', type: 'date' },
      { key: 'clientName', label: 'Vendor Name', type: 'text', required: true },
      { key: 'clientAddress', label: 'Vendor Address', type: 'textarea' },
      { key: 'items', label: 'Order Items', type: 'lineitems', default: [
        { description: 'Office chair', quantity: 4, rate: 250 },
      ]},
      { key: 'taxRate', label: 'Tax Rate (%)', type: 'number', default: 0 },
      { key: 'notes', label: 'Shipping / Notes', type: 'textarea' },
    ],
    po: true,
  },

  // ───────── PERSONAL ─────────
  {
    id: 'resume',
    title: 'Modern Resume',
    category: 'personal',
    description: 'Clean, ATS-friendly resume with summary, experience, education, and skills.',
    icon: 'IdCard',
    popular: true,
    type: 'resume',
    fields: [
      { key: 'fullName', label: 'Full Name', type: 'text', required: true, placeholder: 'Jane Doe' },
      { key: 'title', label: 'Professional Title', type: 'text', placeholder: 'Senior Product Designer' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'San Francisco, CA' },
      { key: 'website', label: 'Website / LinkedIn', type: 'text' },
      { key: 'summary', label: 'Summary', type: 'textarea', placeholder: 'Brief 2-3 sentence professional summary.' },
      { key: 'experience', label: 'Experience', type: 'textarea', placeholder: 'Job Title — Company (2020–Present)\n• Achievement 1\n• Achievement 2\n\nPrevious Role — Company (2018–2020)\n• Achievement' },
      { key: 'education', label: 'Education', type: 'textarea', placeholder: 'B.S. Computer Science — Stanford University, 2019' },
      { key: 'skills', label: 'Skills (comma-separated)', type: 'text', placeholder: 'React, TypeScript, Figma, Python' },
    ],
  },
  {
    id: 'cover-letter',
    title: 'Cover Letter',
    category: 'personal',
    description: 'Personalized cover letter to accompany your resume.',
    icon: 'Mail',
    type: 'document',
    fields: [
      { key: 'fullName', label: 'Your Full Name', type: 'text', required: true },
      { key: 'email', label: 'Your Email', type: 'text' },
      { key: 'phone', label: 'Your Phone', type: 'text' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
      { key: 'hiringManager', label: 'Hiring Manager', type: 'text', default: 'Hiring Manager' },
      { key: 'company', label: 'Company Name', type: 'text', required: true },
      { key: 'position', label: 'Position Applied For', type: 'text', required: true },
      { key: 'body', label: 'Letter Body (your pitch)', type: 'textarea', placeholder: 'Write 2–3 paragraphs about your experience and why you\'re a great fit.' },
    ],
    body: [
      { type: 'p', text: '{{fullName}}' },
      { type: 'p', text: '{{email}} • {{phone}}' },
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Dear {{hiringManager}},' },
      { type: 'p', text: 'I am writing to express my interest in the {{position}} role at {{company}}.' },
      { type: 'p', text: '{{body}}' },
      { type: 'p', text: 'Thank you for considering my application. I look forward to the opportunity to discuss how I can contribute to {{company}}.' },
      { type: 'spacer' },
      { type: 'p', text: 'Sincerely,' },
      { type: 'p', text: '{{fullName}}' },
    ],
  },

  // ───────── HR ─────────
  {
    id: 'offer-letter',
    title: 'Job Offer Letter',
    category: 'hr',
    description: 'Formal offer letter outlining role, compensation, and start date.',
    icon: 'FileSignature',
    type: 'document',
    fields: [
      { key: 'companyName', label: 'Company Name', type: 'text', required: true },
      { key: 'candidateName', label: 'Candidate Name', type: 'text', required: true },
      { key: 'position', label: 'Position Title', type: 'text', required: true },
      { key: 'startDate', label: 'Start Date', type: 'date' },
      { key: 'salary', label: 'Annual Salary ($)', type: 'number', default: 80000 },
      { key: 'reportingTo', label: 'Reports To', type: 'text' },
      { key: 'benefits', label: 'Benefits Summary', type: 'textarea', placeholder: 'Health insurance, 401(k) matching, 20 days PTO, remote-friendly.' },
      { key: 'offerDate', label: 'Letter Date', type: 'date', default: today() },
    ],
    body: [
      { type: 'h1', text: 'OFFER OF EMPLOYMENT' },
      { type: 'p', text: '{{offerDate}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Dear {{candidateName}},' },
      { type: 'p', text: 'We are delighted to offer you the position of {{position}} at {{companyName}}, with a start date of {{startDate}}. You will report to {{reportingTo}}.' },
      { type: 'h2', text: 'Compensation' },
      { type: 'p', text: 'Your annual base salary will be ${{salary}}, paid according to the standard company payroll schedule.' },
      { type: 'h2', text: 'Benefits' },
      { type: 'p', text: '{{benefits}}' },
      { type: 'h2', text: 'Acceptance' },
      { type: 'p', text: 'To accept this offer, please sign below and return a copy to us. We look forward to welcoming you to the team.' },
      { type: 'sign', labels: ['{{companyName}} (Authorized Signatory)', 'Candidate: {{candidateName}}'] },
    ],
  },
  {
    id: 'employment-verification',
    title: 'Employment Verification Letter',
    category: 'hr',
    description: 'Confirms an employee\'s position, dates, and salary for third parties.',
    icon: 'BadgeCheck',
    type: 'document',
    fields: [
      { key: 'companyName', label: 'Company Name', type: 'text', required: true },
      { key: 'employeeName', label: 'Employee Name', type: 'text', required: true },
      { key: 'position', label: 'Position', type: 'text', required: true },
      { key: 'startDate', label: 'Employment Start Date', type: 'date' },
      { key: 'salary', label: 'Annual Salary ($)', type: 'number' },
      { key: 'recipient', label: 'Recipient (e.g., bank, landlord)', type: 'text', default: 'To Whom It May Concern' },
      { key: 'date', label: 'Letter Date', type: 'date', default: today() },
      { key: 'signerName', label: 'Your Name', type: 'text' },
      { key: 'signerTitle', label: 'Your Title', type: 'text', default: 'HR Manager' },
    ],
    body: [
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: '{{recipient}},' },
      { type: 'p', text: 'This letter confirms that {{employeeName}} is currently employed at {{companyName}} as a {{position}} since {{startDate}}. Their current annual salary is ${{salary}}.' },
      { type: 'p', text: 'Should you require any further information, please do not hesitate to contact me.' },
      { type: 'spacer' },
      { type: 'p', text: 'Sincerely,' },
      { type: 'p', text: '{{signerName}}' },
      { type: 'p', text: '{{signerTitle}}, {{companyName}}' },
    ],
  },

  // ───────── REAL ESTATE ─────────
  {
    id: 'rental-agreement',
    title: 'Residential Rental Agreement',
    category: 'real-estate',
    description: 'Month-to-month or fixed-term residential lease agreement.',
    icon: 'Home',
    popular: true,
    type: 'document',
    fields: [
      { key: 'landlord', label: 'Landlord Name', type: 'text', required: true },
      { key: 'tenant', label: 'Tenant Name', type: 'text', required: true },
      { key: 'propertyAddress', label: 'Property Address', type: 'textarea', required: true },
      { key: 'startDate', label: 'Lease Start Date', type: 'date', default: today() },
      { key: 'endDate', label: 'Lease End Date', type: 'date' },
      { key: 'rent', label: 'Monthly Rent ($)', type: 'number', default: 2000 },
      { key: 'deposit', label: 'Security Deposit ($)', type: 'number', default: 2000 },
      { key: 'paymentDay', label: 'Rent Due (Day of Month)', type: 'number', default: 1 },
      { key: 'additionalTerms', label: 'Additional Terms', type: 'textarea', placeholder: 'No pets. Smoking prohibited inside the unit.' },
    ],
    body: [
      { type: 'h1', text: 'RESIDENTIAL RENTAL AGREEMENT' },
      { type: 'p', text: 'This Residential Rental Agreement ("Agreement") is entered into between {{landlord}} ("Landlord") and {{tenant}} ("Tenant") for the property located at:' },
      { type: 'p', text: '{{propertyAddress}}' },
      { type: 'h2', text: 'Term' },
      { type: 'p', text: 'The lease term begins on {{startDate}} and ends on {{endDate}}.' },
      { type: 'h2', text: 'Rent' },
      { type: 'p', text: 'Tenant agrees to pay rent of ${{rent}} per month, due on the {{paymentDay}} of each month.' },
      { type: 'h2', text: 'Security Deposit' },
      { type: 'p', text: 'Tenant shall pay a security deposit of ${{deposit}} prior to taking possession.' },
      { type: 'h2', text: 'Additional Terms' },
      { type: 'p', text: '{{additionalTerms}}' },
      { type: 'sign', labels: ['Landlord: {{landlord}}', 'Tenant: {{tenant}}'] },
    ],
  },

  // ───────── EDUCATION ─────────
  {
    id: 'certificate',
    title: 'Certificate of Achievement',
    category: 'education',
    description: 'Beautiful certificate to recognize completion or achievement.',
    icon: 'Award',
    type: 'document',
    centered: true,
    fields: [
      { key: 'recipient', label: 'Recipient Name', type: 'text', required: true, placeholder: 'Alex Johnson' },
      { key: 'achievement', label: 'Achievement Description', type: 'textarea', placeholder: 'Successful completion of the Advanced Web Development Program' },
      { key: 'organization', label: 'Organization', type: 'text', default: 'The Online Academy' },
      { key: 'date', label: 'Date Awarded', type: 'date', default: today() },
      { key: 'signerName', label: 'Signer Name', type: 'text', default: 'Director' },
    ],
    body: [
      { type: 'h1', text: 'CERTIFICATE OF ACHIEVEMENT' },
      { type: 'p', text: 'This certificate is proudly presented to' },
      { type: 'h2', text: '{{recipient}}' },
      { type: 'p', text: 'in recognition of' },
      { type: 'p', text: '{{achievement}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Awarded on {{date}} by {{organization}}.' },
      { type: 'sign', labels: ['{{signerName}}'] },
    ],
  },
  {
    id: 'permission-slip',
    title: 'Field Trip Permission Slip',
    category: 'education',
    description: 'Parent/guardian permission slip for school activities and field trips.',
    icon: 'GraduationCap',
    type: 'document',
    fields: [
      { key: 'schoolName', label: 'School Name', type: 'text', required: true },
      { key: 'studentName', label: 'Student Name', type: 'text', required: true },
      { key: 'grade', label: 'Grade / Class', type: 'text' },
      { key: 'tripDescription', label: 'Trip Description', type: 'textarea', placeholder: 'Visit to the Natural History Museum.' },
      { key: 'tripDate', label: 'Trip Date', type: 'date' },
      { key: 'departTime', label: 'Departure Time', type: 'text', default: '9:00 AM' },
      { key: 'returnTime', label: 'Return Time', type: 'text', default: '2:00 PM' },
      { key: 'cost', label: 'Cost ($)', type: 'number', default: 0 },
      { key: 'teacherName', label: 'Teacher Name', type: 'text' },
    ],
    body: [
      { type: 'h1', text: 'FIELD TRIP PERMISSION SLIP' },
      { type: 'p', text: '{{schoolName}}' },
      { type: 'h2', text: 'Trip Details' },
      { type: 'p', text: '{{tripDescription}}' },
      { type: 'p', text: 'Date: {{tripDate}}  •  Depart: {{departTime}}  •  Return: {{returnTime}}' },
      { type: 'p', text: 'Cost: ${{cost}}' },
      { type: 'p', text: 'Teacher: {{teacherName}}' },
      { type: 'spacer' },
      { type: 'p', text: 'I, the parent/guardian of {{studentName}} ({{grade}}), give permission for my child to participate in the above field trip and acknowledge any associated risks.' },
      { type: 'sign', labels: ['Parent/Guardian Signature', 'Date'] },
    ],
  },

  // ───────── LEGAL (expanded) ─────────
  {
    id: 'nda-oneway',
    title: 'One-Way NDA (Unilateral)',
    category: 'legal',
    description: 'Protect confidential info disclosed by one party to another (one-way only).',
    icon: 'ShieldCheck',
    type: 'document',
    fields: [
      { key: 'discloser', label: 'Disclosing Party', type: 'text', required: true, placeholder: 'Acme Inc.' },
      { key: 'recipient', label: 'Receiving Party', type: 'text', required: true, placeholder: 'Contractor LLC' },
      { key: 'effectiveDate', label: 'Effective Date', type: 'date', default: today() },
      { key: 'purpose', label: 'Purpose of Disclosure', type: 'textarea', placeholder: 'Evaluation of potential vendor relationship.' },
      { key: 'duration', label: 'Confidentiality Duration (years)', type: 'number', default: 3 },
      { key: 'state', label: 'Governing State/Country', type: 'text', default: 'Delaware, USA' },
    ],
    body: [
      { type: 'h1', text: 'UNILATERAL NON-DISCLOSURE AGREEMENT' },
      { type: 'p', text: 'This Non-Disclosure Agreement ("Agreement") is entered into on {{effectiveDate}} between {{discloser}} ("Disclosing Party") and {{recipient}} ("Receiving Party").' },
      { type: 'h2', text: '1. Purpose' },
      { type: 'p', text: 'The Disclosing Party will share confidential information with the Receiving Party for the following purpose: {{purpose}}' },
      { type: 'h2', text: '2. Obligations of Receiving Party' },
      { type: 'p', text: 'The Receiving Party agrees to: (a) hold all Confidential Information in strict confidence, (b) use it solely for the stated purpose, (c) not disclose it to any third party without prior written consent, and (d) protect it with the same degree of care used for its own confidential information (and at minimum, reasonable care).' },
      { type: 'h2', text: '3. Term' },
      { type: 'p', text: 'This Agreement remains in effect for {{duration}} year(s) from the Effective Date. Obligations of confidentiality survive termination.' },
      { type: 'h2', text: '4. Governing Law' },
      { type: 'p', text: 'This Agreement is governed by the laws of {{state}}.' },
      { type: 'sign', labels: ['Disclosing Party: {{discloser}}', 'Receiving Party: {{recipient}}'] },
    ],
  },
  {
    id: 'demand-letter',
    title: 'Demand Letter',
    category: 'legal',
    description: 'Formal demand for payment or action before legal proceedings.',
    icon: 'AlertTriangle',
    type: 'document',
    fields: [
      { key: 'senderName', label: 'Your Name', type: 'text', required: true },
      { key: 'senderAddress', label: 'Your Address', type: 'textarea' },
      { key: 'recipientName', label: 'Recipient Name', type: 'text', required: true },
      { key: 'recipientAddress', label: 'Recipient Address', type: 'textarea' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
      { key: 'amount', label: 'Amount Owed ($)', type: 'number' },
      { key: 'description', label: 'Description of Dispute', type: 'textarea', placeholder: 'On Jan 12, 2026 you agreed to pay $4,500 for design work delivered Feb 1. Despite multiple reminders, payment is still outstanding.' },
      { key: 'deadlineDays', label: 'Response Deadline (days)', type: 'number', default: 14 },
      { key: 'consequences', label: 'Consequences If Ignored', type: 'textarea', default: 'I will pursue all available legal remedies, including filing suit in small claims court.' },
    ],
    body: [
      { type: 'p', text: '{{senderName}}' },
      { type: 'p', text: '{{senderAddress}}' },
      { type: 'spacer' },
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: '{{recipientName}}' },
      { type: 'p', text: '{{recipientAddress}}' },
      { type: 'spacer' },
      { type: 'h2', text: 'RE: Formal Demand for Payment' },
      { type: 'p', text: 'Dear {{recipientName}},' },
      { type: 'p', text: 'This letter serves as a formal demand regarding the following matter:' },
      { type: 'p', text: '{{description}}' },
      { type: 'p', text: 'The amount currently due and owing is ${{amount}}.' },
      { type: 'p', text: 'You have {{deadlineDays}} days from receipt of this letter to remit payment in full or contact me to arrange resolution. If I do not hear from you by that date:' },
      { type: 'p', text: '{{consequences}}' },
      { type: 'p', text: 'I would prefer to resolve this matter amicably. Please contact me promptly.' },
      { type: 'spacer' },
      { type: 'p', text: 'Sincerely,' },
      { type: 'p', text: '{{senderName}}' },
    ],
  },
  {
    id: 'power-of-attorney',
    title: 'Power of Attorney',
    category: 'legal',
    description: 'Authorize someone to act on your behalf for specified matters.',
    icon: 'KeyRound',
    type: 'document',
    fields: [
      { key: 'principal', label: 'Principal (You)', type: 'text', required: true },
      { key: 'principalAddress', label: 'Principal Address', type: 'textarea' },
      { key: 'agent', label: 'Agent / Attorney-in-Fact', type: 'text', required: true },
      { key: 'agentAddress', label: 'Agent Address', type: 'textarea' },
      { key: 'powersGranted', label: 'Powers Granted', type: 'textarea', placeholder: 'Manage my bank accounts, file tax returns, and sign real estate documents on my behalf.' },
      { key: 'startDate', label: 'Effective Date', type: 'date', default: today() },
      { key: 'endDate', label: 'Expiration Date', type: 'date' },
      { key: 'state', label: 'Governing State', type: 'text', default: 'California, USA' },
    ],
    body: [
      { type: 'h1', text: 'POWER OF ATTORNEY' },
      { type: 'p', text: 'I, {{principal}}, residing at {{principalAddress}}, hereby appoint {{agent}}, residing at {{agentAddress}}, as my true and lawful Attorney-in-Fact ("Agent").' },
      { type: 'h2', text: 'Powers Granted' },
      { type: 'p', text: '{{powersGranted}}' },
      { type: 'h2', text: 'Term' },
      { type: 'p', text: 'This Power of Attorney becomes effective on {{startDate}} and shall remain in effect until {{endDate}}, unless revoked earlier in writing.' },
      { type: 'h2', text: 'Governing Law' },
      { type: 'p', text: 'This document shall be governed by the laws of {{state}}.' },
      { type: 'p', text: 'I revoke any previous Powers of Attorney inconsistent with this document.' },
      { type: 'sign', labels: ['Principal: {{principal}}', 'Witness'] },
    ],
  },
  {
    id: 'loan-agreement',
    title: 'Personal Loan Agreement',
    category: 'legal',
    description: 'Document a loan between individuals with repayment terms and interest.',
    icon: 'HandCoins',
    type: 'document',
    fields: [
      { key: 'lender', label: 'Lender Name', type: 'text', required: true },
      { key: 'borrower', label: 'Borrower Name', type: 'text', required: true },
      { key: 'amount', label: 'Loan Amount ($)', type: 'number', default: 1000 },
      { key: 'interestRate', label: 'Annual Interest Rate (%)', type: 'number', default: 5 },
      { key: 'termMonths', label: 'Term (months)', type: 'number', default: 12 },
      { key: 'monthlyPayment', label: 'Monthly Payment ($)', type: 'number' },
      { key: 'startDate', label: 'Start Date', type: 'date', default: today() },
      { key: 'paymentDay', label: 'Payment Due Day', type: 'number', default: 1 },
      { key: 'defaultTerms', label: 'Default / Late Terms', type: 'textarea', default: 'Payments more than 10 days late incur a $25 fee. Default after 60 days entitles the Lender to demand full repayment.' },
    ],
    body: [
      { type: 'h1', text: 'PERSONAL LOAN AGREEMENT' },
      { type: 'p', text: 'This Loan Agreement is entered into on {{startDate}} between {{lender}} ("Lender") and {{borrower}} ("Borrower").' },
      { type: 'h2', text: '1. Loan Amount & Interest' },
      { type: 'p', text: 'Lender shall loan to Borrower the principal sum of ${{amount}}, with simple interest at {{interestRate}}% per annum.' },
      { type: 'h2', text: '2. Repayment' },
      { type: 'p', text: 'Borrower shall repay the loan in {{termMonths}} monthly installments of ${{monthlyPayment}}, due on the {{paymentDay}} of each month, beginning {{startDate}}.' },
      { type: 'h2', text: '3. Prepayment' },
      { type: 'p', text: 'Borrower may prepay the loan, in whole or in part, at any time without penalty.' },
      { type: 'h2', text: '4. Default' },
      { type: 'p', text: '{{defaultTerms}}' },
      { type: 'sign', labels: ['Lender: {{lender}}', 'Borrower: {{borrower}}'] },
    ],
  },

  // ───────── BUSINESS (expanded) ─────────
  {
    id: 'freelance-contract',
    title: 'Freelance / Independent Contractor Agreement',
    category: 'business',
    description: 'Standard contract between a client and freelancer/contractor.',
    icon: 'FileSignature',
    type: 'document',
    popular: true,
    fields: [
      { key: 'client', label: 'Client Name', type: 'text', required: true },
      { key: 'contractor', label: 'Contractor Name', type: 'text', required: true },
      { key: 'projectTitle', label: 'Project Title', type: 'text', required: true, placeholder: 'Website redesign' },
      { key: 'scope', label: 'Scope of Work', type: 'textarea', placeholder: 'Design and deliver a 5-page responsive marketing site, including 2 rounds of revisions.' },
      { key: 'startDate', label: 'Start Date', type: 'date', default: today() },
      { key: 'endDate', label: 'End Date', type: 'date' },
      { key: 'fee', label: 'Total Fee ($)', type: 'number', default: 5000 },
      { key: 'paymentTerms', label: 'Payment Terms', type: 'textarea', default: '50% deposit upon signing, 50% upon final delivery. Invoices due within 14 days.' },
      { key: 'ipOwnership', label: 'IP Ownership', type: 'textarea', default: 'Upon final payment, all deliverables become the exclusive property of the Client. Contractor may showcase the work in portfolios.' },
      { key: 'terminationTerms', label: 'Termination Terms', type: 'textarea', default: 'Either party may terminate with 14 days written notice. Client pays for work completed up to termination.' },
    ],
    body: [
      { type: 'h1', text: 'INDEPENDENT CONTRACTOR AGREEMENT' },
      { type: 'p', text: 'This Agreement is entered into on {{startDate}} between {{client}} ("Client") and {{contractor}} ("Contractor") for the project "{{projectTitle}}".' },
      { type: 'h2', text: '1. Scope of Work' },
      { type: 'p', text: '{{scope}}' },
      { type: 'h2', text: '2. Timeline' },
      { type: 'p', text: 'Work begins on {{startDate}} and is targeted for completion by {{endDate}}.' },
      { type: 'h2', text: '3. Compensation' },
      { type: 'p', text: 'Client agrees to pay Contractor a total of ${{fee}} for the project. {{paymentTerms}}' },
      { type: 'h2', text: '4. Independent Contractor Status' },
      { type: 'p', text: 'Contractor is an independent contractor, not an employee. Contractor is responsible for own taxes, insurance, and benefits.' },
      { type: 'h2', text: '5. Intellectual Property' },
      { type: 'p', text: '{{ipOwnership}}' },
      { type: 'h2', text: '6. Termination' },
      { type: 'p', text: '{{terminationTerms}}' },
      { type: 'sign', labels: ['Client: {{client}}', 'Contractor: {{contractor}}'] },
    ],
  },
  {
    id: 'quote',
    title: 'Quote / Estimate',
    category: 'business',
    description: 'Itemized price quote to send before invoicing.',
    icon: 'FileSpreadsheet',
    type: 'invoice',
    quote: true,
    fields: [
      { key: 'businessName', label: 'Your Business Name', type: 'text', required: true },
      { key: 'businessAddress', label: 'Your Address', type: 'textarea' },
      { key: 'businessEmail', label: 'Your Email', type: 'text' },
      { key: 'invoiceNumber', label: 'Quote #', type: 'text', default: 'Q-001' },
      { key: 'invoiceDate', label: 'Quote Date', type: 'date', default: today() },
      { key: 'dueDate', label: 'Valid Until', type: 'date' },
      { key: 'clientName', label: 'Quote For (Client)', type: 'text', required: true },
      { key: 'clientAddress', label: 'Client Address', type: 'textarea' },
      { key: 'items', label: 'Quoted Items', type: 'lineitems', default: [
        { description: 'Logo design package', quantity: 1, rate: 1200 },
      ]},
      { key: 'taxRate', label: 'Tax Rate (%)', type: 'number', default: 0 },
      { key: 'notes', label: 'Notes', type: 'textarea', default: 'This quote is valid for 30 days. To accept, reply or sign and return.' },
    ],
  },
  {
    id: 'sow',
    title: 'Statement of Work (SOW)',
    category: 'business',
    description: 'Detailed project SOW with objectives, deliverables, and milestones.',
    icon: 'ListChecks',
    type: 'document',
    fields: [
      { key: 'client', label: 'Client Name', type: 'text', required: true },
      { key: 'contractor', label: 'Service Provider', type: 'text', required: true },
      { key: 'projectTitle', label: 'Project Title', type: 'text', required: true },
      { key: 'startDate', label: 'Start Date', type: 'date', default: today() },
      { key: 'endDate', label: 'Target End Date', type: 'date' },
      { key: 'objectives', label: 'Objectives', type: 'textarea', placeholder: '• Launch new e-commerce store\n• Migrate existing customers\n• Achieve 99.9% uptime' },
      { key: 'deliverables', label: 'Deliverables', type: 'textarea', placeholder: '• Design system\n• Storefront with Stripe integration\n• Admin dashboard' },
      { key: 'milestones', label: 'Milestones & Timeline', type: 'textarea', placeholder: 'Phase 1: Discovery (week 1-2)\nPhase 2: Design (week 3-5)\nPhase 3: Build (week 6-10)\nPhase 4: Launch (week 11-12)' },
      { key: 'fee', label: 'Total Project Fee ($)', type: 'number', default: 25000 },
      { key: 'assumptions', label: 'Assumptions & Out-of-Scope', type: 'textarea', placeholder: 'Client will provide brand assets within 5 business days. Hosting and 3rd-party services not included.' },
    ],
    body: [
      { type: 'h1', text: 'STATEMENT OF WORK' },
      { type: 'p', text: 'Project: {{projectTitle}}' },
      { type: 'p', text: 'Between {{client}} ("Client") and {{contractor}} ("Provider")' },
      { type: 'p', text: 'Effective: {{startDate}} → {{endDate}}' },
      { type: 'h2', text: '1. Objectives' },
      { type: 'p', text: '{{objectives}}' },
      { type: 'h2', text: '2. Deliverables' },
      { type: 'p', text: '{{deliverables}}' },
      { type: 'h2', text: '3. Milestones & Timeline' },
      { type: 'p', text: '{{milestones}}' },
      { type: 'h2', text: '4. Total Project Fee' },
      { type: 'p', text: '${{fee}}' },
      { type: 'h2', text: '5. Assumptions & Out-of-Scope' },
      { type: 'p', text: '{{assumptions}}' },
      { type: 'sign', labels: ['Client: {{client}}', 'Provider: {{contractor}}'] },
    ],
  },

  // ───────── HR (expanded) ─────────
  {
    id: 'resignation-letter',
    title: 'Resignation Letter',
    category: 'hr',
    description: 'Professional two-week notice resignation letter.',
    icon: 'LogOut',
    type: 'document',
    popular: true,
    fields: [
      { key: 'employeeName', label: 'Your Name', type: 'text', required: true },
      { key: 'position', label: 'Your Position', type: 'text', required: true },
      { key: 'companyName', label: 'Company Name', type: 'text', required: true },
      { key: 'managerName', label: 'Manager Name', type: 'text', default: 'Hiring Manager' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
      { key: 'lastDay', label: 'Last Day of Work', type: 'date' },
      { key: 'gratitude', label: 'Gratitude / Closing Note', type: 'textarea', default: 'I am grateful for the opportunities to grow and contribute during my time here. I will do everything I can to ensure a smooth transition.' },
    ],
    body: [
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Dear {{managerName}},' },
      { type: 'p', text: 'Please accept this letter as formal notice of my resignation from my position as {{position}} at {{companyName}}. My last day of work will be {{lastDay}}.' },
      { type: 'p', text: '{{gratitude}}' },
      { type: 'p', text: 'Please let me know how I can help during the transition period.' },
      { type: 'spacer' },
      { type: 'p', text: 'Sincerely,' },
      { type: 'p', text: '{{employeeName}}' },
    ],
  },
  {
    id: 'termination-letter',
    title: 'Employee Termination Letter',
    category: 'hr',
    description: 'Formal letter terminating an employee\'s employment.',
    icon: 'UserX',
    type: 'document',
    fields: [
      { key: 'companyName', label: 'Company Name', type: 'text', required: true },
      { key: 'employeeName', label: 'Employee Name', type: 'text', required: true },
      { key: 'position', label: 'Position', type: 'text', required: true },
      { key: 'date', label: 'Letter Date', type: 'date', default: today() },
      { key: 'lastDay', label: 'Last Day of Employment', type: 'date' },
      { key: 'reason', label: 'Reason for Termination', type: 'textarea', placeholder: 'Role elimination due to organizational restructuring.' },
      { key: 'finalPayDate', label: 'Final Pay Date', type: 'date' },
      { key: 'severance', label: 'Severance Details', type: 'textarea', placeholder: '2 weeks of severance pay, continuation of health benefits through end of month.' },
      { key: 'returnItems', label: 'Items to Return', type: 'textarea', default: 'Laptop, access badge, company credit card, and any company-owned materials.' },
      { key: 'signerName', label: 'Your Name', type: 'text' },
      { key: 'signerTitle', label: 'Your Title', type: 'text', default: 'HR Director' },
    ],
    body: [
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Dear {{employeeName}},' },
      { type: 'p', text: 'This letter confirms our recent conversation regarding the end of your employment with {{companyName}}, effective {{lastDay}}, from your position as {{position}}.' },
      { type: 'h2', text: 'Reason' },
      { type: 'p', text: '{{reason}}' },
      { type: 'h2', text: 'Final Pay & Benefits' },
      { type: 'p', text: 'Your final paycheck will be issued on {{finalPayDate}}. {{severance}}' },
      { type: 'h2', text: 'Return of Company Property' },
      { type: 'p', text: '{{returnItems}}' },
      { type: 'p', text: 'We thank you for your contributions and wish you success in your future endeavors.' },
      { type: 'spacer' },
      { type: 'p', text: 'Sincerely,' },
      { type: 'p', text: '{{signerName}}, {{signerTitle}}' },
      { type: 'p', text: '{{companyName}}' },
    ],
  },

  // ───────── REAL ESTATE (expanded) ─────────
  {
    id: 'sublease-agreement',
    title: 'Sublease Agreement',
    category: 'real-estate',
    description: 'Sublet your rented property to a subtenant with landlord-friendly terms.',
    icon: 'KeySquare',
    type: 'document',
    fields: [
      { key: 'tenant', label: 'Original Tenant (Sublessor)', type: 'text', required: true },
      { key: 'subtenant', label: 'Subtenant (Sublessee)', type: 'text', required: true },
      { key: 'landlord', label: 'Landlord Name', type: 'text' },
      { key: 'propertyAddress', label: 'Property Address', type: 'textarea', required: true },
      { key: 'startDate', label: 'Sublease Start Date', type: 'date', default: today() },
      { key: 'endDate', label: 'Sublease End Date', type: 'date' },
      { key: 'rent', label: 'Monthly Rent ($)', type: 'number' },
      { key: 'deposit', label: 'Security Deposit ($)', type: 'number' },
      { key: 'additionalTerms', label: 'Additional Terms', type: 'textarea', default: 'Subtenant agrees to all terms of the original lease, a copy of which is attached. Landlord consent is required and acknowledged below.' },
    ],
    body: [
      { type: 'h1', text: 'SUBLEASE AGREEMENT' },
      { type: 'p', text: 'This Sublease is between {{tenant}} ("Sublessor") and {{subtenant}} ("Sublessee") for the property located at:' },
      { type: 'p', text: '{{propertyAddress}}' },
      { type: 'h2', text: 'Term' },
      { type: 'p', text: 'From {{startDate}} through {{endDate}}.' },
      { type: 'h2', text: 'Rent & Deposit' },
      { type: 'p', text: 'Sublessee shall pay ${{rent}} per month to Sublessor and a security deposit of ${{deposit}} prior to occupancy.' },
      { type: 'h2', text: 'Original Lease' },
      { type: 'p', text: '{{additionalTerms}}' },
      { type: 'sign', labels: ['Sublessor: {{tenant}}', 'Sublessee: {{subtenant}}', 'Landlord Consent: {{landlord}}'] },
    ],
  },
  {
    id: 'eviction-notice',
    title: 'Notice to Quit (Eviction)',
    category: 'real-estate',
    description: 'Formal notice for a tenant to vacate or cure a lease violation.',
    icon: 'AlertOctagon',
    type: 'document',
    fields: [
      { key: 'landlord', label: 'Landlord Name', type: 'text', required: true },
      { key: 'tenant', label: 'Tenant Name', type: 'text', required: true },
      { key: 'propertyAddress', label: 'Property Address', type: 'textarea', required: true },
      { key: 'date', label: 'Notice Date', type: 'date', default: today() },
      { key: 'reason', label: 'Reason for Notice', type: 'textarea', placeholder: 'Failure to pay rent for the months of January and February.' },
      { key: 'noticeDays', label: 'Days to Vacate or Cure', type: 'number', default: 30 },
      { key: 'amountDue', label: 'Amount Due (if applicable, $)', type: 'number' },
      { key: 'cureOption', label: 'Option to Cure', type: 'textarea', default: 'You may avoid eviction by paying the full amount due within the notice period.' },
    ],
    body: [
      { type: 'h1', text: 'NOTICE TO QUIT' },
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: 'TO: {{tenant}}' },
      { type: 'p', text: 'PROPERTY: {{propertyAddress}}' },
      { type: 'spacer' },
      { type: 'p', text: 'You are hereby notified that you are in violation of your lease for the following reason:' },
      { type: 'p', text: '{{reason}}' },
      { type: 'p', text: 'Amount currently due: ${{amountDue}}' },
      { type: 'h2', text: 'Cure or Vacate' },
      { type: 'p', text: '{{cureOption}}' },
      { type: 'p', text: 'You have {{noticeDays}} days from receipt of this notice to either cure the violation or vacate the premises. Failure to do so will result in legal action to recover possession of the property.' },
      { type: 'sign', labels: ['Landlord: {{landlord}}'] },
    ],
  },
  {
    id: 'roommate-agreement',
    title: 'Roommate Agreement',
    category: 'real-estate',
    description: 'Set clear expectations between roommates: rent, chores, guests, quiet hours.',
    icon: 'Users2',
    type: 'document',
    fields: [
      { key: 'roommateA', label: 'Roommate A', type: 'text', required: true },
      { key: 'roommateB', label: 'Roommate B', type: 'text', required: true },
      { key: 'propertyAddress', label: 'Property Address', type: 'textarea', required: true },
      { key: 'startDate', label: 'Start Date', type: 'date', default: today() },
      { key: 'rent', label: 'Total Monthly Rent ($)', type: 'number' },
      { key: 'rentSplit', label: 'Rent Split', type: 'text', default: '50/50' },
      { key: 'utilitySplit', label: 'Utilities Split', type: 'textarea', default: 'Electric, gas, water, internet split 50/50 and paid by the 5th of each month.' },
      { key: 'choreSchedule', label: 'Chore Schedule', type: 'textarea', default: 'Roommates alternate weekly: cleaning bathroom, kitchen, common areas. Each roommate handles own dishes within 24 hours.' },
      { key: 'quietHours', label: 'Quiet Hours', type: 'text', default: '10 PM – 7 AM weekdays, midnight – 8 AM weekends' },
      { key: 'guestPolicy', label: 'Guest Policy', type: 'textarea', default: 'Overnight guests max 3 nights per week. Long-term stays (>1 week) require advance notice and mutual agreement.' },
    ],
    body: [
      { type: 'h1', text: 'ROOMMATE AGREEMENT' },
      { type: 'p', text: 'This agreement between {{roommateA}} and {{roommateB}}, for the residence at {{propertyAddress}}, beginning {{startDate}}.' },
      { type: 'h2', text: 'Rent & Utilities' },
      { type: 'p', text: 'Total monthly rent: ${{rent}}. Split: {{rentSplit}}.' },
      { type: 'p', text: '{{utilitySplit}}' },
      { type: 'h2', text: 'Chores' },
      { type: 'p', text: '{{choreSchedule}}' },
      { type: 'h2', text: 'Quiet Hours' },
      { type: 'p', text: '{{quietHours}}' },
      { type: 'h2', text: 'Guests' },
      { type: 'p', text: '{{guestPolicy}}' },
      { type: 'sign', labels: ['Roommate A: {{roommateA}}', 'Roommate B: {{roommateB}}'] },
    ],
  },

  // ───────── PERSONAL (expanded) ─────────
  {
    id: 'reference-letter',
    title: 'Reference Letter',
    category: 'personal',
    description: 'Professional reference letter to recommend a colleague or former employee.',
    icon: 'ThumbsUp',
    type: 'document',
    fields: [
      { key: 'referrerName', label: 'Your Name', type: 'text', required: true },
      { key: 'referrerTitle', label: 'Your Title / Company', type: 'text' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
      { key: 'refereeName', label: 'Person You\'re Recommending', type: 'text', required: true },
      { key: 'relationship', label: 'Your Relationship', type: 'textarea', placeholder: 'I was {{refereeName}}\'s manager at Acme for 3 years from 2021–2024.' },
      { key: 'qualities', label: 'Key Qualities & Achievements', type: 'textarea', placeholder: 'Reliable, exceptional communication, led a team of 6 that shipped...' },
      { key: 'recommendation', label: 'Recommendation Statement', type: 'textarea', default: 'I recommend {{refereeName}} without reservation and would gladly work with them again.' },
      { key: 'contactEmail', label: 'Your Contact Email', type: 'text' },
    ],
    body: [
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: 'To Whom It May Concern,' },
      { type: 'p', text: 'I am writing to recommend {{refereeName}}. {{relationship}}' },
      { type: 'p', text: '{{qualities}}' },
      { type: 'p', text: '{{recommendation}}' },
      { type: 'p', text: 'Please feel free to contact me at {{contactEmail}} for any questions.' },
      { type: 'spacer' },
      { type: 'p', text: 'Sincerely,' },
      { type: 'p', text: '{{referrerName}}' },
      { type: 'p', text: '{{referrerTitle}}' },
    ],
  },

  // ═════════════════════════════════════════════
  //            JOB SEEKER PACK
  // ═════════════════════════════════════════════
  {
    id: 'cv',
    title: 'Curriculum Vitae (CV)',
    category: 'personal',
    pack: 'job-seeker',
    description: 'Comprehensive academic/professional CV with research, publications, and awards.',
    icon: 'ScrollText',
    type: 'document',
    fields: [
      { key: 'fullName', label: 'Full Name', type: 'text', required: true, placeholder: 'Dr. Jane Doe' },
      { key: 'title', label: 'Title / Field', type: 'text', placeholder: 'Research Scientist · Molecular Biology' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Boston, MA' },
      { key: 'website', label: 'Website / ORCID / LinkedIn', type: 'text' },
      { key: 'profile', label: 'Professional Profile', type: 'textarea', placeholder: 'Brief 3-4 sentence overview of your expertise and focus.' },
      { key: 'experience', label: 'Professional Experience', type: 'textarea', placeholder: 'Role — Institution (2020–Present)\n• Key responsibility / achievement' },
      { key: 'education', label: 'Education', type: 'textarea', placeholder: 'Ph.D. Molecular Biology — Harvard University, 2018\nB.S. Biology — UCLA, 2012' },
      { key: 'publications', label: 'Publications & Research', type: 'textarea', placeholder: 'Doe J. et al. (2024). Title. Journal, Vol(Issue), pages.' },
      { key: 'skills', label: 'Skills & Certifications', type: 'textarea', placeholder: 'Technical skills, languages, certifications, memberships.' },
      { key: 'awards', label: 'Awards & Honors', type: 'textarea', placeholder: 'Grant / fellowship / award — Year' },
    ],
    body: [
      { type: 'h1', text: '{{fullName}}' },
      { type: 'p', text: '{{title}}' },
      { type: 'p', text: '{{email}}  •  {{phone}}  •  {{location}}  •  {{website}}' },
      { type: 'h2', text: 'Professional Profile' },
      { type: 'p', text: '{{profile}}' },
      { type: 'h2', text: 'Professional Experience' },
      { type: 'p', text: '{{experience}}' },
      { type: 'h2', text: 'Education' },
      { type: 'p', text: '{{education}}' },
      { type: 'h2', text: 'Publications & Research' },
      { type: 'p', text: '{{publications}}' },
      { type: 'h2', text: 'Skills & Certifications' },
      { type: 'p', text: '{{skills}}' },
      { type: 'h2', text: 'Awards & Honors' },
      { type: 'p', text: '{{awards}}' },
    ],
  },
  {
    id: 'thank-you-email',
    title: 'Post-Interview Thank-You Email',
    category: 'personal',
    pack: 'job-seeker',
    description: 'Follow-up note to send after a job interview to leave a strong impression.',
    icon: 'MailCheck',
    type: 'document',
    fields: [
      { key: 'yourName', label: 'Your Name', type: 'text', required: true },
      { key: 'recipientName', label: 'Interviewer Name', type: 'text', required: true, default: 'Hiring Manager' },
      { key: 'position', label: 'Position', type: 'text', required: true, placeholder: 'Product Manager' },
      { key: 'company', label: 'Company', type: 'text', required: true },
      { key: 'highlights', label: 'What Stood Out / Why You\'re a Fit', type: 'textarea', placeholder: 'I was especially excited about the roadmap you described, and my experience launching...' },
      { key: 'contactInfo', label: 'Your Contact Info', type: 'text', placeholder: 'jane@email.com · (555) 123-4567' },
    ],
    body: [
      { type: 'p', text: 'Subject: Thank you — {{position}} interview' },
      { type: 'spacer' },
      { type: 'p', text: 'Dear {{recipientName}},' },
      { type: 'p', text: 'Thank you for taking the time to meet with me about the {{position}} role at {{company}}. I truly enjoyed our conversation and learning more about the team and what you\'re building.' },
      { type: 'p', text: '{{highlights}}' },
      { type: 'p', text: 'Please don\'t hesitate to reach out if there\'s anything further I can provide. I look forward to the next steps.' },
      { type: 'spacer' },
      { type: 'p', text: 'Best regards,' },
      { type: 'p', text: '{{yourName}}' },
      { type: 'p', text: '{{contactInfo}}' },
    ],
  },
  {
    id: 'reference-request',
    title: 'Reference Request Letter',
    category: 'personal',
    pack: 'job-seeker',
    description: 'Politely ask a former manager or colleague to serve as a professional reference.',
    icon: 'UserPlus',
    type: 'document',
    fields: [
      { key: 'yourName', label: 'Your Name', type: 'text', required: true },
      { key: 'recipientName', label: 'Reference\'s Name', type: 'text', required: true },
      { key: 'relationship', label: 'How You Worked Together', type: 'textarea', placeholder: 'You were my manager on the growth team at Acme from 2021–2024.' },
      { key: 'role', label: 'Role / Opportunity You\'re Pursuing', type: 'text', placeholder: 'Senior Marketing Manager at BrightCo' },
      { key: 'deadline', label: 'Deadline (if any)', type: 'date' },
      { key: 'details', label: 'What You\'d Like Them to Highlight', type: 'textarea', placeholder: 'If you could speak to my leadership on the Q3 campaign, that would be wonderful.' },
      { key: 'contactInfo', label: 'Your Contact Info', type: 'text' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
    ],
    body: [
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Dear {{recipientName}},' },
      { type: 'p', text: 'I hope you\'re doing well. {{relationship}} Because of that, I\'m reaching out to ask whether you\'d be willing to serve as a professional reference for me.' },
      { type: 'p', text: 'I\'m currently pursuing the following opportunity: {{role}}. {{details}}' },
      { type: 'p', text: 'If possible, I\'d be grateful to have your response by {{deadline}}. I\'m happy to share my latest resume and any additional context that would help.' },
      { type: 'p', text: 'Thank you so much for considering — I really appreciate your support.' },
      { type: 'spacer' },
      { type: 'p', text: 'Warm regards,' },
      { type: 'p', text: '{{yourName}}' },
      { type: 'p', text: '{{contactInfo}}' },
    ],
  },
  {
    id: 'salary-negotiation',
    title: 'Salary Negotiation Letter',
    category: 'personal',
    pack: 'job-seeker',
    description: 'Professionally counter a job offer and make the case for higher compensation.',
    icon: 'TrendingUp',
    type: 'document',
    fields: [
      { key: 'yourName', label: 'Your Name', type: 'text', required: true },
      { key: 'hiringManager', label: 'Hiring Manager / Recruiter', type: 'text', required: true, default: 'Hiring Manager' },
      { key: 'company', label: 'Company', type: 'text', required: true },
      { key: 'position', label: 'Position', type: 'text', required: true },
      { key: 'offeredSalary', label: 'Offered Salary ($)', type: 'number' },
      { key: 'requestedSalary', label: 'Requested Salary ($)', type: 'number' },
      { key: 'justification', label: 'Your Justification', type: 'textarea', placeholder: 'Based on my 7 years of experience, the market range for this role, and the revenue I drove at my last company...' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
    ],
    body: [
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Dear {{hiringManager}},' },
      { type: 'p', text: 'Thank you so much for offering me the {{position}} role at {{company}}. I\'m genuinely excited about the opportunity and the chance to contribute to the team.' },
      { type: 'p', text: 'Before I formally accept, I\'d like to discuss the base compensation. The offer stands at ${{offeredSalary}}, and I\'d like to respectfully propose ${{requestedSalary}}.' },
      { type: 'p', text: '{{justification}}' },
      { type: 'p', text: 'I\'m confident we can find a number that reflects the value I\'ll bring, and I remain very enthusiastic about joining {{company}}. I\'m happy to discuss further at your convenience.' },
      { type: 'spacer' },
      { type: 'p', text: 'Sincerely,' },
      { type: 'p', text: '{{yourName}}' },
    ],
  },

  // ═════════════════════════════════════════════
  //            SMALL-BUSINESS PACK
  // ═════════════════════════════════════════════
  {
    id: 'service-agreement',
    title: 'Service Agreement',
    category: 'business',
    pack: 'small-business',
    description: 'General agreement between a service provider and a client for ongoing work.',
    icon: 'Handshake',
    type: 'document',
    fields: [
      { key: 'provider', label: 'Service Provider', type: 'text', required: true, placeholder: 'Acme Studio LLC' },
      { key: 'client', label: 'Client Name', type: 'text', required: true },
      { key: 'effectiveDate', label: 'Effective Date', type: 'date', default: today() },
      { key: 'services', label: 'Services Provided', type: 'textarea', required: true, placeholder: 'Monthly social media management, including content creation and reporting.' },
      { key: 'fee', label: 'Fee ($)', type: 'number', default: 1500 },
      { key: 'paymentTerms', label: 'Payment Terms', type: 'textarea', default: 'Billed monthly. Invoices are due within 14 days of receipt. Late payments accrue a 1.5% monthly fee.' },
      { key: 'term', label: 'Term & Renewal', type: 'textarea', default: 'This agreement runs month-to-month and renews automatically until cancelled.' },
      { key: 'terminationTerms', label: 'Termination', type: 'textarea', default: 'Either party may terminate with 30 days written notice. Client pays for all work completed to the termination date.' },
      { key: 'state', label: 'Governing State', type: 'text', default: 'California, USA' },
    ],
    body: [
      { type: 'h1', text: 'SERVICE AGREEMENT' },
      { type: 'p', text: 'This Service Agreement ("Agreement") is entered into on {{effectiveDate}} between {{provider}} ("Provider") and {{client}} ("Client").' },
      { type: 'h2', text: '1. Services' },
      { type: 'p', text: '{{services}}' },
      { type: 'h2', text: '2. Compensation' },
      { type: 'p', text: 'Client agrees to pay Provider ${{fee}} for the services described. {{paymentTerms}}' },
      { type: 'h2', text: '3. Term & Renewal' },
      { type: 'p', text: '{{term}}' },
      { type: 'h2', text: '4. Termination' },
      { type: 'p', text: '{{terminationTerms}}' },
      { type: 'h2', text: '5. Governing Law' },
      { type: 'p', text: 'This Agreement shall be governed by the laws of {{state}}.' },
      { type: 'sign', labels: ['Provider: {{provider}}', 'Client: {{client}}'] },
    ],
  },
  {
    id: 'onboarding-questionnaire',
    title: 'Client Onboarding Questionnaire',
    category: 'business',
    pack: 'small-business',
    description: 'Collect everything you need from a new client before kicking off a project.',
    icon: 'ClipboardCheck',
    type: 'document',
    fields: [
      { key: 'businessName', label: 'Your Business Name', type: 'text', required: true },
      { key: 'introNote', label: 'Welcome Note', type: 'textarea', default: 'Welcome aboard! To help us deliver your project smoothly, please take a few minutes to answer the questions below.' },
      { key: 'questions', label: 'Questions', type: 'textarea', default: '1. What are the main goals for this project?\n2. Who is your target audience?\n3. Who are your top 3 competitors?\n4. What does success look like in 6 months?\n5. What is your budget range and timeline?\n6. Who is the main point of contact and decision-maker?\n7. Do you have existing brand assets (logo, colors, fonts)?\n8. Is there anything we should absolutely avoid?' },
      { key: 'contactEmail', label: 'Return To (Email)', type: 'text', placeholder: 'hello@acme.com' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
    ],
    body: [
      { type: 'h1', text: 'CLIENT ONBOARDING QUESTIONNAIRE' },
      { type: 'p', text: '{{businessName}}  ·  {{date}}' },
      { type: 'p', text: '{{introNote}}' },
      { type: 'h2', text: 'Your Answers' },
      { type: 'p', text: '{{questions}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Please return this completed form to {{contactEmail}}. Thank you — we\'re excited to work with you!' },
    ],
  },
  {
    id: 'payment-reminder',
    title: 'Payment Reminder Letter',
    category: 'business',
    pack: 'small-business',
    description: 'A friendly but firm nudge for an overdue invoice.',
    icon: 'BellRing',
    type: 'document',
    fields: [
      { key: 'businessName', label: 'Your Business Name', type: 'text', required: true },
      { key: 'clientName', label: 'Client Name', type: 'text', required: true },
      { key: 'invoiceNumber', label: 'Invoice #', type: 'text', default: 'INV-001' },
      { key: 'amount', label: 'Amount Due ($)', type: 'number' },
      { key: 'dueDate', label: 'Original Due Date', type: 'date' },
      { key: 'note', label: 'Additional Note', type: 'textarea', default: 'If you\'ve already sent payment, please disregard this message and accept our thanks.' },
      { key: 'contactEmail', label: 'Your Contact Email', type: 'text' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
    ],
    body: [
      { type: 'p', text: '{{date}}' },
      { type: 'spacer' },
      { type: 'p', text: 'Dear {{clientName}},' },
      { type: 'p', text: 'I hope you\'re well. This is a friendly reminder that Invoice {{invoiceNumber}} for ${{amount}}, originally due on {{dueDate}}, is now outstanding.' },
      { type: 'p', text: 'We\'d appreciate it if you could arrange payment at your earliest convenience. If you have any questions about the invoice or need another copy, just reply and we\'ll send it over right away.' },
      { type: 'p', text: '{{note}}' },
      { type: 'p', text: 'Thank you for your prompt attention and for your continued business.' },
      { type: 'spacer' },
      { type: 'p', text: 'Best regards,' },
      { type: 'p', text: '{{businessName}}' },
      { type: 'p', text: '{{contactEmail}}' },
    ],
  },

  // ═════════════════════════════════════════════
  //            PERSONAL ESSENTIALS PACK
  // ═════════════════════════════════════════════
  {
    id: 'will-worksheet',
    title: 'Simple Will Worksheet',
    category: 'personal',
    pack: 'personal-essentials',
    description: 'Organize your final wishes — executors, beneficiaries, and guardians — in one place.',
    icon: 'FileHeart',
    type: 'document',
    fields: [
      { key: 'fullName', label: 'Full Legal Name', type: 'text', required: true },
      { key: 'address', label: 'Your Address', type: 'textarea' },
      { key: 'executor', label: 'Executor (manages your estate)', type: 'text', required: true },
      { key: 'alternateExecutor', label: 'Alternate Executor', type: 'text' },
      { key: 'guardians', label: 'Guardian(s) for Minor Children', type: 'textarea', placeholder: 'Name any guardians you wish to appoint for children under 18.' },
      { key: 'specificGifts', label: 'Specific Gifts / Bequests', type: 'textarea', placeholder: 'e.g. My watch to my brother John; $5,000 to the local animal shelter.' },
      { key: 'residuary', label: 'Residuary Estate (everything else goes to…)', type: 'textarea', placeholder: 'I leave the remainder of my estate to my spouse, or equally to my children.' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
    ],
    body: [
      { type: 'h1', text: 'SIMPLE WILL WORKSHEET' },
      { type: 'p', text: 'This worksheet helps you organize your wishes. It is not a substitute for legal advice — consult an attorney to create a valid, binding will in your jurisdiction.' },
      { type: 'h2', text: 'Testator' },
      { type: 'p', text: 'Name: {{fullName}}' },
      { type: 'p', text: 'Address: {{address}}' },
      { type: 'p', text: 'Date: {{date}}' },
      { type: 'h2', text: 'Executor' },
      { type: 'p', text: 'I appoint {{executor}} as the Executor of my estate. If unable or unwilling to serve, I appoint {{alternateExecutor}} as alternate.' },
      { type: 'h2', text: 'Guardianship' },
      { type: 'p', text: '{{guardians}}' },
      { type: 'h2', text: 'Specific Gifts' },
      { type: 'p', text: '{{specificGifts}}' },
      { type: 'h2', text: 'Residuary Estate' },
      { type: 'p', text: '{{residuary}}' },
      { type: 'sign', labels: ['Testator: {{fullName}}', 'Witness 1', 'Witness 2'] },
    ],
  },
  {
    id: 'vehicle-bill-of-sale',
    title: 'Vehicle Bill of Sale',
    category: 'legal',
    pack: 'personal-essentials',
    description: 'Record the sale of a car, motorcycle, or other vehicle between two parties.',
    icon: 'Car',
    type: 'document',
    fields: [
      { key: 'seller', label: 'Seller Name', type: 'text', required: true },
      { key: 'sellerAddress', label: 'Seller Address', type: 'textarea' },
      { key: 'buyer', label: 'Buyer Name', type: 'text', required: true },
      { key: 'buyerAddress', label: 'Buyer Address', type: 'textarea' },
      { key: 'year', label: 'Year', type: 'text', placeholder: '2018' },
      { key: 'make', label: 'Make', type: 'text', placeholder: 'Honda' },
      { key: 'model', label: 'Model', type: 'text', placeholder: 'Civic LX' },
      { key: 'vin', label: 'VIN', type: 'text', placeholder: '1HGFC2F59JH123456' },
      { key: 'odometer', label: 'Odometer (miles)', type: 'text', placeholder: '45,000' },
      { key: 'color', label: 'Color', type: 'text', placeholder: 'Blue' },
      { key: 'price', label: 'Sale Price ($)', type: 'number', default: 5000 },
      { key: 'saleDate', label: 'Date of Sale', type: 'date', default: today() },
    ],
    body: [
      { type: 'h1', text: 'VEHICLE BILL OF SALE' },
      { type: 'p', text: 'On {{saleDate}}, {{seller}} ("Seller"), of {{sellerAddress}}, sells and transfers ownership of the vehicle described below to {{buyer}} ("Buyer"), of {{buyerAddress}}.' },
      { type: 'h2', text: 'Vehicle Details' },
      { type: 'p', text: 'Year: {{year}}    Make: {{make}}    Model: {{model}}' },
      { type: 'p', text: 'VIN: {{vin}}    Color: {{color}}    Odometer: {{odometer}} miles' },
      { type: 'h2', text: 'Sale Terms' },
      { type: 'p', text: 'The total sale price is ${{price}}, paid in full. The Seller certifies lawful ownership, free of all liens, and sells the vehicle "AS-IS" without warranty of any kind.' },
      { type: 'sign', labels: ['Seller: {{seller}}', 'Buyer: {{buyer}}'] },
    ],
  },
  {
    id: 'babysitting-agreement',
    title: 'Babysitting Agreement',
    category: 'personal',
    pack: 'personal-essentials',
    description: 'Set clear expectations with a babysitter: schedule, pay, duties, and emergency info.',
    icon: 'Baby',
    type: 'document',
    fields: [
      { key: 'parentName', label: 'Parent / Guardian Name', type: 'text', required: true },
      { key: 'sitterName', label: 'Babysitter Name', type: 'text', required: true },
      { key: 'children', label: 'Children (names & ages)', type: 'textarea', placeholder: 'Mia (6), Leo (3)' },
      { key: 'schedule', label: 'Schedule', type: 'textarea', placeholder: 'Weekdays 3:00 PM – 6:00 PM; occasional weekend evenings with notice.' },
      { key: 'rate', label: 'Pay Rate', type: 'text', placeholder: '$20/hour, paid weekly' },
      { key: 'duties', label: 'Duties & Responsibilities', type: 'textarea', default: 'Supervise children, prepare simple meals and snacks, help with homework, follow bedtime routine, and tidy up play areas.' },
      { key: 'houseRules', label: 'House Rules', type: 'textarea', default: 'No screen time after 7 PM. No visitors. Lock doors when inside. Text updates every 2 hours.' },
      { key: 'emergencyContact', label: 'Emergency Contact', type: 'textarea', placeholder: 'Parent cell, pediatrician, nearby relative.' },
      { key: 'date', label: 'Date', type: 'date', default: today() },
    ],
    body: [
      { type: 'h1', text: 'BABYSITTING AGREEMENT' },
      { type: 'p', text: 'This agreement is made on {{date}} between {{parentName}} ("Parent/Guardian") and {{sitterName}} ("Babysitter").' },
      { type: 'h2', text: 'Children' },
      { type: 'p', text: '{{children}}' },
      { type: 'h2', text: 'Schedule' },
      { type: 'p', text: '{{schedule}}' },
      { type: 'h2', text: 'Compensation' },
      { type: 'p', text: '{{rate}}' },
      { type: 'h2', text: 'Duties' },
      { type: 'p', text: '{{duties}}' },
      { type: 'h2', text: 'House Rules' },
      { type: 'p', text: '{{houseRules}}' },
      { type: 'h2', text: 'Emergency Information' },
      { type: 'p', text: '{{emergencyContact}}' },
      { type: 'sign', labels: ['Parent/Guardian: {{parentName}}', 'Babysitter: {{sitterName}}'] },
    ],
  },
  {
    id: 'pet-agreement',
    title: 'Pet Care Agreement',
    category: 'personal',
    pack: 'personal-essentials',
    description: 'Arrange pet sitting or boarding — care instructions, vet info, and emergency authorization.',
    icon: 'PawPrint',
    type: 'document',
    fields: [
      { key: 'ownerName', label: 'Pet Owner Name', type: 'text', required: true },
      { key: 'caretakerName', label: 'Caretaker / Sitter Name', type: 'text', required: true },
      { key: 'petDetails', label: 'Pet(s) (name, type, breed, age)', type: 'textarea', placeholder: 'Max — Golden Retriever, 4 yrs' },
      { key: 'startDate', label: 'Start Date', type: 'date', default: today() },
      { key: 'endDate', label: 'End Date', type: 'date' },
      { key: 'careInstructions', label: 'Care & Feeding Instructions', type: 'textarea', default: 'Feed twice daily (8 AM / 6 PM), 1 cup per meal. Two walks per day. Fresh water always available.' },
      { key: 'vetInfo', label: 'Veterinarian Info', type: 'textarea', placeholder: 'Clinic name, address, phone.' },
      { key: 'emergencyAuth', label: 'Emergency Authorization', type: 'textarea', default: 'Caretaker is authorized to seek veterinary care in an emergency. Owner agrees to reimburse reasonable medical expenses.' },
      { key: 'fee', label: 'Fee ($)', type: 'number', default: 40 },
      { key: 'date', label: 'Date', type: 'date', default: today() },
    ],
    body: [
      { type: 'h1', text: 'PET CARE AGREEMENT' },
      { type: 'p', text: 'This agreement is made on {{date}} between {{ownerName}} ("Owner") and {{caretakerName}} ("Caretaker").' },
      { type: 'h2', text: 'Pet(s)' },
      { type: 'p', text: '{{petDetails}}' },
      { type: 'h2', text: 'Care Period' },
      { type: 'p', text: 'From {{startDate}} to {{endDate}}.' },
      { type: 'h2', text: 'Care Instructions' },
      { type: 'p', text: '{{careInstructions}}' },
      { type: 'h2', text: 'Veterinarian' },
      { type: 'p', text: '{{vetInfo}}' },
      { type: 'h2', text: 'Emergency Authorization' },
      { type: 'p', text: '{{emergencyAuth}}' },
      { type: 'h2', text: 'Compensation' },
      { type: 'p', text: 'The agreed fee is ${{fee}} for the care period described above.' },
      { type: 'sign', labels: ['Owner: {{ownerName}}', 'Caretaker: {{caretakerName}}'] },
    ],
  },
  {
    id: 'personal-budget',
    title: 'Personal Budget Sheet',
    category: 'personal',
    pack: 'personal-essentials',
    description: 'Map out monthly income, expenses, and savings goals on one clean page.',
    icon: 'PiggyBank',
    type: 'document',
    fields: [
      { key: 'name', label: 'Your Name', type: 'text', required: true },
      { key: 'month', label: 'Month / Period', type: 'text', placeholder: 'June 2026' },
      { key: 'monthlyIncome', label: 'Total Monthly Income ($)', type: 'number', default: 4000 },
      { key: 'incomeSources', label: 'Income Sources', type: 'textarea', placeholder: 'Salary — $3,500\nFreelance — $500' },
      { key: 'fixedExpenses', label: 'Fixed Expenses', type: 'textarea', placeholder: 'Rent — $1,500\nUtilities — $200\nInsurance — $150' },
      { key: 'variableExpenses', label: 'Variable Expenses', type: 'textarea', placeholder: 'Groceries — $400\nDining out — $200\nEntertainment — $100' },
      { key: 'savingsGoal', label: 'Savings & Goals', type: 'textarea', placeholder: 'Emergency fund — $300\nVacation — $150' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Cut dining out next month; review subscriptions.' },
    ],
    body: [
      { type: 'h1', text: 'PERSONAL BUDGET' },
      { type: 'p', text: '{{name}}  ·  {{month}}' },
      { type: 'h2', text: 'Income' },
      { type: 'p', text: 'Total Monthly Income: ${{monthlyIncome}}' },
      { type: 'p', text: '{{incomeSources}}' },
      { type: 'h2', text: 'Fixed Expenses' },
      { type: 'p', text: '{{fixedExpenses}}' },
      { type: 'h2', text: 'Variable Expenses' },
      { type: 'p', text: '{{variableExpenses}}' },
      { type: 'h2', text: 'Savings & Goals' },
      { type: 'p', text: '{{savingsGoal}}' },
      { type: 'h2', text: 'Notes' },
      { type: 'p', text: '{{notes}}' },
    ],
  },
];

// Curated starter packs — bundles of related templates for a specific moment.
export const PACKS = [
  {
    slug: 'job-seeker',
    name: 'Job Seeker Pack',
    tagline: 'Everything to land the role — from first application to signed offer.',
    icon: 'Briefcase',
    tint: 'bg-emerald-100 text-emerald-900',
    templateIds: ['cv', 'cover-letter', 'thank-you-email', 'reference-request', 'salary-negotiation'],
  },
  {
    slug: 'small-business',
    name: 'Small-Business Pack',
    tagline: 'Get paid and look professional — contracts, quotes, and invoices.',
    icon: 'Store',
    tint: 'bg-orange-100 text-orange-900',
    templateIds: ['service-agreement', 'onboarding-questionnaire', 'invoice', 'quote', 'purchase-order', 'payment-reminder'],
  },
  {
    slug: 'personal-essentials',
    name: 'Personal Essentials',
    tagline: 'Life admin, sorted — the everyday documents everyone eventually needs.',
    icon: 'Home',
    tint: 'bg-rose-100 text-rose-900',
    templateIds: ['will-worksheet', 'vehicle-bill-of-sale', 'babysitting-agreement', 'pet-agreement', 'personal-budget'],
  },
];

export const PACK_PRICE = 9;

export const getTemplate = (id) => TEMPLATES.find(t => t.id === id);
export const getByCategory = (slug) => TEMPLATES.filter(t => t.category === slug);
export const getPopular = () => TEMPLATES.filter(t => t.popular);
export const getPack = (slug) => PACKS.find(p => p.slug === slug);
export const getPackTemplates = (slug) => {
  const pack = getPack(slug);
  if (!pack) return [];
  return pack.templateIds.map(id => getTemplate(id)).filter(Boolean);
};